import { useUploadStore } from "@components/FileUpload/uploadStore";
import {
  useBayHireEntryState,
  type BayHireEntryState,
} from "@features/bay-hire/hooks/useBayHireEntryState";
import { useBayHireRequestFormStore } from "@features/bay-hire/store/useBayHireRequestForm.store";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export interface UseBayHireContainerResult {
  t: ReturnType<typeof useTranslation>["t"];
  dir: "ltr" | "rtl";
  isOperationsUser: boolean;
  companyId: string;
  entryState: BayHireEntryState;
  handleNewRequest: () => void;
}

export const useBayHireContainer = (): UseBayHireContainerResult => {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const navigate = useNavigate();
  const { entryState, companyId, isOperationsUser } = useBayHireEntryState();
  const resetForm = useBayHireRequestFormStore((state) => state.resetForm);

  // DFT-0096: with a single, id-less route for every variant, arriving at
  // paths.bayHire.request with no location.state is otherwise ambiguous
  // between "start a new create" and "resume an unfinished resubmit/update/
  // renew" (whatever the persisted store's requestId still points to). "+ Bay
  // Hire" is an unambiguous "start fresh" signal, so it must clear that stale
  // state itself before navigating - see BayHireRequestFormPage.tsx's mount
  // logic and frontend-guidelines.md Section 1's carve-out.
  //
  // DFT-0106: resetForm() only ever clears useBayHireRequestFormStore - it has no knowledge of
  // useUploadStore, so an HOD document uploaded (and left uncleared) in an earlier session would
  // otherwise silently leak into this fresh Create: the form field genuinely resets, but
  // FileUpload's own currentFiles falls back to the surviving useUploadStore entry, disabling
  // the dropzone with no visible way to remove it (value is null, so no Remove UI renders at
  // all). HOD_APPROVAL is the only key BayHireCreateAttachmentUpload (this form's one upload
  // spot) ever writes to.
  const handleNewRequest = () => {
    resetForm();
    useUploadStore.getState().clearFilesForKey("HOD_APPROVAL");
    navigate(`/${paths.bayHire.request}`);
  };

  return {
    t,
    dir,
    isOperationsUser,
    companyId,
    entryState,
    handleNewRequest,
  };
};
